/**
 * Predicate for the vehicle-lookup field. Kept out of the component file so
 * react-refresh stays happy (that file exports components only) and so the
 * rule is importable without pulling a component in.
 */
import type { VehicleLookupResult } from '@/admin/hooks/use-lookups'
import type { FormFieldMeta } from '@/shared/lib/forms/types'

/** True when this field declares the vehicle lookup provider. */
export function isVehicleLookupField(field: FormFieldMeta) {
  return String(field.configuration_data?.lookup_provider ?? '') === 'dvla'
}

/** A lookup response, keyed the way the provider returns it. */
export type VehicleLookupValues = Record<string, string | undefined>

/** The lookup response keyed by the field codes and columns it can fill. */
export function vehicleLookupValues(data: VehicleLookupResult): VehicleLookupValues {
  return {
    make: data.make,
    model: data.model,
    variant: data.variant ?? '',
    vin: data.vin ?? '',
    colour: data.colour,
    year: data.year,
    fuel_type: data.fuel_type,
    engine_type: data.engine_type,
    tax_status: data.tax_status,
    mot_status: data.mot_status,
    month_of_first_registration: data.month_of_first_registration,
    registration: data.registration,
    registration_number: data.registration,
  }
}

/**
 * What every prepopulated field should hold after a lookup.
 *
 * A lookup REPLACES the vehicle's details rather than merging into them, so a
 * field the provider has no answer for is cleared. Leaving it meant correcting
 * a plate kept the previous vehicle's data - the response carries no VIN, so a
 * Kia ended up wearing a Fiat's, with nothing on screen to say so.
 */
export function resolvePrepopulatedValues(
  fields: FormFieldMeta[],
  values: VehicleLookupValues,
): Record<string, string> {
  const next: Record<string, string> = {}
  for (const field of fields) {
    if (!field.configuration_data?.is_prepopulated) continue
    const mapped = String(field.configuration_data?.maps_to_column ?? '')
    next[field.code_name] = values[field.code_name] ?? values[mapped] ?? ''
  }
  return next
}
